import React, { useState, useEffect, useRef, memo } from 'react';
import { Box, Flex, Spinner, Image, Link, Skeleton } from '@chakra-ui/react';
import { getImagensUrlsBatch } from '../../services/api';

const PLACEHOLDER = 'https://www.shutterstock.com/image-illustration/car-parts-auto-spare-shopping-600nw-2033173178.jpg';

// --- Otimizacao de thumbnails via otimizador da loja (Next /_next/image) ---
// Reescreve URLs de imagens hospedadas na loja para WebP redimensionado no tamanho
// realmente exibido (ex.: thumb de 65px nao baixa mais o original de varios MB).
// URLs de outros dominios passam intactas; em erro o componente cai na original.
const IMG_OTIM_HOST = 'www.tagaautopartes.com.br';
const NEXT_IMG_WIDTHS = [16, 32, 48, 64, 96, 128, 256, 384, 640, 750, 828, 1080, 1200, 1920];

const snapLargura = (alvo) => NEXT_IMG_WIDTHS.find((w) => w >= alvo) || NEXT_IMG_WIDTHS[NEXT_IMG_WIDTHS.length - 1];

const pxDe = (valor, fallback) => {
    if (typeof valor === 'number') return valor;
    const m = typeof valor === 'string' ? valor.match(/(\d+)/) : null;
    return m ? Number(m[1]) : fallback;
};

export const otimizarUrl = (url, larguraPx) => {
    if (!url || typeof url !== 'string') return url;
    let parsed;
    try { parsed = new URL(url, window.location.origin); } catch (_) { return url; }
    if (parsed.hostname !== IMG_OTIM_HOST) return url;          // so otimiza imagens da loja
    if (parsed.pathname.startsWith('/_next/image')) return url; // ja otimizada
    const w = snapLargura(Math.ceil(larguraPx));
    const alvo = parsed.pathname + parsed.search;
    return `https://${IMG_OTIM_HOST}/_next/image?url=${encodeURIComponent(alvo)}&w=${w}&q=70`;
};

const STORAGE_KEY = 'produto_imagem_url_cache_v3';
const BANCO_STORAGE_KEY = 'produto_imagem_banco_cache_v3';
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const BANCO_TTL_MS = 6 * 60 * 60 * 1000;

const resolvedUrlCache = new Map();
const pendingResolutions = new Map();
const verifiedUrlCache = new Map();

const invalidationListeners = new Map();
const notificarImagem = (key, url) => {
    const subs = invalidationListeners.get(key);
    if (subs) subs.forEach((fn) => fn(url));
};

const bancoCache = new Map();
const bancoMissCache = new Set();
const bancoPendingPromises = new Map();
let bancoQueue = new Set();
let bancoQueueResolvers = new Map();
let bancoTimer = null;
const BANCO_DEBOUNCE_MS = 80;
const BANCO_BATCH_MAX = 200;

(() => {
    try {
        const raw = localStorage.getItem(BANCO_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.data || !parsed.exp || parsed.exp < Date.now()) {
            localStorage.removeItem(BANCO_STORAGE_KEY);
            return;
        }
        Object.entries(parsed.data).forEach(([k, v]) => {
            if (v === null) bancoMissCache.add(Number(k));
            else bancoCache.set(Number(k), v);
        });
    } catch (_) {}
})();

let bancoPersistScheduled = false;
const scheduleBancoPersist = () => {
    if (bancoPersistScheduled) return;
    bancoPersistScheduled = true;
    setTimeout(() => {
        bancoPersistScheduled = false;
        try {
            const obj = {};
            bancoCache.forEach((v, k) => { obj[k] = v; });
            bancoMissCache.forEach((k) => { if (!(k in obj)) obj[k] = null; });
            localStorage.setItem(BANCO_STORAGE_KEY, JSON.stringify({ data: obj, exp: Date.now() + BANCO_TTL_MS }));
        } catch (_) {}
    }, 1500);
};

const flushBancoQueue = async () => {
    bancoTimer = null;
    const ids = Array.from(bancoQueue);
    const resolvers = bancoQueueResolvers;
    bancoQueue = new Set();
    bancoQueueResolvers = new Map();
    if (ids.length === 0) return;

    try {
        for (let i = 0; i < ids.length; i += BANCO_BATCH_MAX) {
            const slice = ids.slice(i, i + BANCO_BATCH_MAX);
            const res = await getImagensUrlsBatch(slice);
            const mapa = res.data || {};
            slice.forEach((id) => {
                const url = mapa[id] || mapa[String(id)] || null;
                if (url) bancoCache.set(Number(id), url);
                else bancoMissCache.add(Number(id));
                bancoPendingPromises.delete(Number(id));
                const cbs = resolvers.get(Number(id)) || [];
                cbs.forEach((fn) => fn(url));
            });
        }
        scheduleBancoPersist();
    } catch (_) {
        ids.forEach((id) => {
            bancoMissCache.add(Number(id));
            bancoPendingPromises.delete(Number(id));
            const cbs = resolvers.get(Number(id)) || [];
            cbs.forEach((fn) => fn(null));
        });
    }
};

// Revalidacao em segundo plano: a imagem cacheada aparece na hora e, logo depois,
// o app confere no banco se ela mudou. Sem isso a troca de imagem de um produto so
// aparecia em outras telas quando o cache expirava (ate 6h).
const revalidados = new Set();
let revalQueue = new Set();
let revalTimer = null;
const REVAL_DEBOUNCE_MS = 1200;

const flushRevalidacao = async () => {
    revalTimer = null;
    const ids = Array.from(revalQueue);
    revalQueue = new Set();
    if (ids.length === 0) return;

    try {
        for (let i = 0; i < ids.length; i += BANCO_BATCH_MAX) {
            const slice = ids.slice(i, i + BANCO_BATCH_MAX);
            const res = await getImagensUrlsBatch(slice);
            const mapa = res.data || {};
            slice.forEach((id) => {
                const nova = mapa[id] || mapa[String(id)] || null;
                const atual = bancoCache.get(Number(id)) || null;
                if (nova === atual) return;
                atualizarCacheImagem(id, nova);
            });
        }
    } catch (_) {}
};

const agendarRevalidacao = (produtoId) => {
    const id = Number(produtoId);
    if (!id || revalidados.has(id)) return;
    revalidados.add(id);
    revalQueue.add(id);
    if (!revalTimer) revalTimer = setTimeout(flushRevalidacao, REVAL_DEBOUNCE_MS);
};

const buscarUrlNoBanco = (produtoId) => {
    const id = Number(produtoId);
    if (!id) return Promise.resolve(null);
    if (bancoCache.has(id)) {
        agendarRevalidacao(id);
        return Promise.resolve(bancoCache.get(id));
    }
    if (bancoMissCache.has(id)) return Promise.resolve(null);
    if (bancoPendingPromises.has(id)) return bancoPendingPromises.get(id);

    const promise = new Promise((resolve) => {
        const arr = bancoQueueResolvers.get(id) || [];
        arr.push(resolve);
        bancoQueueResolvers.set(id, arr);
        bancoQueue.add(id);
        if (!bancoTimer) bancoTimer = setTimeout(flushBancoQueue, BANCO_DEBOUNCE_MS);
    });
    bancoPendingPromises.set(id, promise);
    return promise;
};

(() => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.data || !parsed.exp || parsed.exp < Date.now()) {
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        Object.entries(parsed.data).forEach(([k, v]) => resolvedUrlCache.set(k, v));
    } catch (_) {}
})();

let persistScheduled = false;
const schedulePersist = () => {
    if (persistScheduled) return;
    persistScheduled = true;
    setTimeout(() => {
        persistScheduled = false;
        try {
            const obj = {};
            resolvedUrlCache.forEach((v, k) => { obj[k] = v; });
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ data: obj, exp: Date.now() + STORAGE_TTL_MS }));
        } catch (_) {}
    }, 1500);
};

export const atualizarCacheImagem = (produtoId, url) => {
    if (produtoId == null) return;
    const idNum = Number(produtoId);
    const key = String(produtoId);
    if (url) {
        bancoCache.set(idNum, url);
        bancoMissCache.delete(idNum);
        resolvedUrlCache.set(idNum, url);
        resolvedUrlCache.set(key, url);
    } else {
        bancoCache.delete(idNum);
        bancoMissCache.delete(idNum);
        resolvedUrlCache.delete(idNum);
        resolvedUrlCache.delete(key);
    }
    bancoPendingPromises.delete(idNum);
    pendingResolutions.delete(idNum);
    pendingResolutions.delete(key);
    scheduleBancoPersist();
    schedulePersist();
    notificarImagem(key, url || null);
};

const resolverImagem = async (produto) => {
    if (!produto) return PLACEHOLDER;
    const produtoId = produto?.produto_id || produto?.sku || produto?.id;
    if (!produtoId) return PLACEHOLDER;
    const urlBanco = await buscarUrlNoBanco(produtoId);
    return urlBanco || PLACEHOLDER;
};

const produtoKeyOf = (produto) => produto?.produto_id || produto?.sku || produto?.id || null;

const escolherUrlDoBanco = (imagens) => {
    if (!Array.isArray(imagens) || imagens.length === 0) return null;
    const ativas = imagens.filter(i => !i.deleted_at && i.status === 'aprovada');
    if (ativas.length === 0) return null;
    const principal = ativas.find(i => i.principal);
    return (principal || ativas[0]).url || null;
};

// Exibe a imagem com skeleton enquanto carrega + fade-in suave + fallback em erro.
// Preserva tamanho/objectFit do estado final (sem regressao visual), so melhora a transicao.
const ImagemComFade = ({ src, alt, imageProps = {}, larguraAlvo }) => {
    const [carregada, setCarregada] = useState(false);
    const srcOtimizado = larguraAlvo ? otimizarUrl(src, larguraAlvo) : src;
    const [srcAtual, setSrcAtual] = useState(srcOtimizado);
    const imgRef = useRef(null);

    const tratarErro = () => {
        // otimizada falhou -> tenta a original crua; original falhou -> placeholder
        if (srcAtual !== src && srcAtual !== PLACEHOLDER) setSrcAtual(src);
        else if (srcAtual !== PLACEHOLDER) setSrcAtual(PLACEHOLDER);
        else setCarregada(true);
    };

    useEffect(() => {
        setCarregada(false);
        setSrcAtual(srcOtimizado);
    }, [srcOtimizado]);

    useEffect(() => {
        const img = imgRef.current;
        if (!img || !img.complete) return;
        if (img.naturalWidth > 0) setCarregada(true);
        else tratarErro();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [srcAtual]);

    return (
        <Flex position="relative" justify="center" align="center" w="full" h="full" overflow="hidden">
            {!carregada && (
                <Skeleton position="absolute" top="0" left="0" w="full" h="full" borderRadius="md" />
            )}
            <Image
                ref={imgRef}
                src={srcAtual}
                alt={alt}
                onLoad={() => setCarregada(true)}
                onError={tratarErro}
                opacity={carregada ? 1 : 0}
                transition="opacity 0.3s ease-in-out"
                loading="lazy"
                decoding="async"
                {...imageProps}
            />
        </Flex>
    );
};

const ProdutoImagem = memo(({ produto, origem, width, height, zoomable = false, imagemAPI = '', imagens = null }) => {
    const usarImagemApi = imagemAPI?.length > 10;
    const usarImagensBanco = Array.isArray(imagens);
    const cacheKey = produtoKeyOf(produto);

    const urlDoBanco = usarImagensBanco ? escolherUrlDoBanco(imagens) : null;

    const initialResolved = usarImagemApi
        ? imagemAPI
        : usarImagensBanco
            ? (urlDoBanco || PLACEHOLDER)
            : (cacheKey ? resolvedUrlCache.get(cacheKey) : null);

    const [caminhoImagem, setCaminhoImagem] = useState(initialResolved || null);
    const [carregando, setCarregando] = useState(!initialResolved);
    const [isZoomVisible, setIsZoomVisible] = useState(false);

    // largura real do thumbnail (2x p/ telas retina) usada para pedir a versao redimensionada
    const larguraAlvo = pxDe(width, 96) * 2;

    useEffect(() => {
        let ativo = true;

        if (usarImagemApi) {
            setCaminhoImagem(imagemAPI);
            setCarregando(false);
            return () => { ativo = false; };
        }

        if (usarImagensBanco) {
            setCaminhoImagem(urlDoBanco || PLACEHOLDER);
            setCarregando(false);
            return () => { ativo = false; };
        }

        if (!cacheKey) {
            setCaminhoImagem(PLACEHOLDER);
            setCarregando(false);
            return () => { ativo = false; };
        }

        const cached = resolvedUrlCache.get(cacheKey);
        if (cached) {
            setCaminhoImagem(cached);
            setCarregando(false);
            agendarRevalidacao(cacheKey);
            return () => { ativo = false; };
        }

        setCarregando(true);

        let promise = pendingResolutions.get(cacheKey);
        if (!promise) {
            promise = resolverImagem(produto).then((url) => {
                resolvedUrlCache.set(cacheKey, url);
                pendingResolutions.delete(cacheKey);
                schedulePersist();
                return url;
            });
            pendingResolutions.set(cacheKey, promise);
        }

        promise.then((url) => {
            if (!ativo) return;
            setCaminhoImagem(url);
            setCarregando(false);
        });

        return () => { ativo = false; };
    }, [cacheKey, usarImagemApi, imagemAPI, produto, usarImagensBanco, urlDoBanco]);

    useEffect(() => {
        if (!cacheKey || usarImagemApi || usarImagensBanco) return undefined;
        const key = String(cacheKey);
        const aoInvalidar = (url) => {
            if (url) { setCaminhoImagem(url); setCarregando(false); return; }
            setCarregando(true);
            resolverImagem(produto).then((u) => {
                resolvedUrlCache.set(cacheKey, u);
                setCaminhoImagem(u);
                setCarregando(false);
            });
        };
        let set = invalidationListeners.get(key);
        if (!set) { set = new Set(); invalidationListeners.set(key, set); }
        set.add(aoInvalidar);
        return () => {
            set.delete(aoInvalidar);
            if (set.size === 0) invalidationListeners.delete(key);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheKey, usarImagemApi, usarImagensBanco]);

    const handleMouseEnter = () => {
        if (zoomable && !carregando && caminhoImagem) setIsZoomVisible(true);
    };
    const handleMouseLeave = () => {
        if (zoomable) setIsZoomVisible(false);
    };

    const imageContent = carregando ? (
        <Spinner size="md" />
    ) : usarImagemApi ? (
        <Flex justifyContent="center" alignItems="center" w="full" h="full" bg="white" boxShadow="md" borderRadius="md">
            <ImagemComFade
                src={caminhoImagem}
                alt={produto?.nome || 'Imagem do produto'}
                larguraAlvo={larguraAlvo}
                imageProps={{ maxW: "100%", maxH: "100%", objectFit: "contain" }}
            />
        </Flex>
    ) : (
        <ImagemComFade
            src={caminhoImagem}
            alt={produto?.nome || 'Imagem do produto'}
            larguraAlvo={larguraAlvo}
            imageProps={{ boxShadow: "md", borderRadius: "md", w: "full", h: "full", objectFit: "cover" }}
        />
    );

    const imageComponent = (
        <Flex justifyContent="center" align="center" w={width} h={height}>
            {imageContent}
        </Flex>
    );

    return (
        <Box position="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {origem === 'Mercado Livre' || origem === 'Mercado Shops' ? (
                <Link href={`https://produto.mercadolivre.com.br/MLB-${produto.id_ml?.substring(3)}`} isExternal>
                    {imageComponent}
                </Link>
            ) : (
                imageComponent
            )}

            {isZoomVisible && (
                <Box
                    position="fixed"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    zIndex="popover"
                    bg="rgba(255, 255, 255, 0.95)"
                    p={4}
                    borderRadius="lg"
                    boxShadow="dark-lg"
                    pointerEvents="none"
                >
                    <Image
                        src={otimizarUrl(caminhoImagem, 1080)}
                        alt={`Zoom de ${produto?.nome || 'Imagem do produto'}`}
                        maxW="80vw"
                        maxH="80vh"
                        objectFit="contain"
                    />
                </Box>
            )}
        </Box>
    );
}, (prev, next) => {
    const prevImg = Array.isArray(prev.imagens) ? prev.imagens.length : -1;
    const nextImg = Array.isArray(next.imagens) ? next.imagens.length : -1;
    const prevHash = Array.isArray(prev.imagens)
        ? prev.imagens.map(i => `${i.id}:${i.principal ? 1 : 0}:${i.status}:${i.deleted_at ? 'd' : 'a'}`).join('|')
        : '';
    const nextHash = Array.isArray(next.imagens)
        ? next.imagens.map(i => `${i.id}:${i.principal ? 1 : 0}:${i.status}:${i.deleted_at ? 'd' : 'a'}`).join('|')
        : '';
    return (
        produtoKeyOf(prev.produto) === produtoKeyOf(next.produto)
        && prev.origem === next.origem
        && prev.width === next.width
        && prev.height === next.height
        && prev.zoomable === next.zoomable
        && prev.imagemAPI === next.imagemAPI
        && prevImg === nextImg
        && prevHash === nextHash
    );
});

export default ProdutoImagem;
